import axios from 'axios';
import { getApiOrigin } from '@utils/apiOrigin';

const API_BASE_URL = getApiOrigin();
const BILLING_INVOICES_PATH = '/api/v1/billing/invoices';
const BILLING_PLANS_PATH = '/api/v1/billing/plans';
const BILLING_TRIAL_FACILITY_GATE_PATH = '/api/v1/billing/trial-facility-gate';

export const TRIAL_FACILITY_GATE_SESSION_KEY = 'trial_facility_gate';

export type TrialFacilityGateResult = {
  has_active_trial: boolean;
  restrict_to_single_facility: boolean;
  show_verification_page: boolean;
  multi_facility_unlocked: boolean;
  trial_plan_purchased_id?: number | null;
  reason?: string;
};

export type BillingSubscriptionStatus = 'Active' | 'Payment Required' | 'Cancelling';

export interface BillingRecord {
  id: string;
  facilityName: string;
  selectedPlan: string;
  status: 'Active' | 'Inactive';
  /** Active subscriptions grid display status */
  subscriptionStatus?: BillingSubscriptionStatus;
  /** Short date for Cancelling chip, e.g. Mar 25, 2027 */
  cancelsOnLabel?: string;
  /** Raw status string from billing plans API */
  apiStatus?: string | null;
  cancellationDate?: string | null;
  cancelAtPeriodEnd?: boolean;
  isCancelled?: boolean;
  invoiceDate: string;
  expiryDate: string;
  invoiceId: string;
  invoiceUrl?: string;
  /** Derived from API for subscriptions UI */
  billingFrequency?: string;
  paymentMethod?: string;
  /** Active subscription row: IDs for facility-cancellation API */
  userAssociatedFacilityId?: number;
  /** MCM Stripe customer id for update payment method */
  customerId?: number;
  facilityId?: number;
  planId?: number;
  planPurchasedId?: number;
  /** Raw plan end from API (ISO or parseable) for cancellation end_date */
  planEndDateIso?: string | null;
  /** Numeric `id` from plans API row when distinct from display `id` */
  sourcePlanRowId?: number;
  /** Original plans API row for cancellation ID fallback */
  rawPlanRow?: Record<string, unknown>;
  /** Billing History: API plan period (raw) */
  planStartDate?: string | null;
  planEndDate?: string | null;
  /** Billing History: formatted from plan_start_date / plan_end_date */
  billingPeriodDisplay?: string;
}

export interface BillingStats {
  totalRevenue: number;
  activeFacilities: number;
  inactiveFacilities: number;
  pendingPayments: number;
}

// Service class for Billing API calls
class BillingService {
  /**
   * Get invoices for the current user
   * @param page - 1-based page number
   * @param size - page size
   * @param userId - user id
   * @param filters - optional filters object
   */
  async getInvoices(params: {
    page: number;
    size: number;
    user_id: number | string;
    filters?: Record<string, unknown>;
  }): Promise<any> {
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token') || '';

      const queryParams = new URLSearchParams();
      queryParams.set('page', String(params.page));
      queryParams.set('size', String(params.size));
      queryParams.set('user_id', String(params.user_id));
      if (params.filters && Object.keys(params.filters).length > 0) {
        queryParams.set('filters', JSON.stringify(params.filters));
      }

      const url = `${API_BASE_URL}${BILLING_INVOICES_PATH}?${queryParams.toString()}`;

      const response = await axios.get(url, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
      });

      return response.data;
    } catch (error) {
      console.error('Error fetching invoices:', error);
      throw error;
    }
  }

  /**
   * Get active subscription plans for the current user
   * Used by the Active Subscriptions tab in the Billing page.
   */
  async getPlans(params: {
    page: number;
    size: number;
    user_id: number | string;
    filters?: Record<string, unknown>;
  }): Promise<any> {
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token') || '';

      const effectiveFilters: Record<string, unknown> = {
        is_active: 1,
        ...(params.filters || {}),
      };

      const queryParams = new URLSearchParams();
      queryParams.set('page', String(params.page));
      queryParams.set('size', String(params.size));
      queryParams.set('user_id', String(params.user_id));
      queryParams.set('filters', JSON.stringify(effectiveFilters));

      const url = `${API_BASE_URL}${BILLING_PLANS_PATH}?${queryParams.toString()}`;

      const response = await axios.get(url, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
      });

      return response.data;
    } catch (error) {
      console.error('Error fetching billing plans:', error);
      throw error;
    }
  }

  /**
   * MCM trial gate: active trial → single facility + verification;
   * no active trial → multi-facility unlocked.
   */
  async getTrialFacilityGate(
    userId: number | string,
  ): Promise<TrialFacilityGateResult> {
    const token =
      localStorage.getItem('access_token') || localStorage.getItem('token') || '';
    const queryParams = new URLSearchParams();
    queryParams.set('user_id', String(userId));
    const url = `${API_BASE_URL}${BILLING_TRIAL_FACILITY_GATE_PATH}?${queryParams.toString()}`;
    const response = await axios.get(url, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
      },
    });
    const body = response.data;
    const result = (body?.result ?? body?.data ?? body) as TrialFacilityGateResult;
    const hasActiveTrial = result?.has_active_trial === true;
    const multiUnlocked =
      typeof result?.multi_facility_unlocked === 'boolean'
        ? result.multi_facility_unlocked
        : !hasActiveTrial;
    const normalized: TrialFacilityGateResult = {
      has_active_trial: hasActiveTrial,
      restrict_to_single_facility: !multiUnlocked,
      show_verification_page: !multiUnlocked,
      multi_facility_unlocked: multiUnlocked,
      trial_plan_purchased_id: result?.trial_plan_purchased_id ?? null,
      reason: result?.reason,
    };
    try {
      sessionStorage.setItem(
        TRIAL_FACILITY_GATE_SESSION_KEY,
        JSON.stringify(normalized),
      );
    } catch {
      // ignore quota / private mode
    }
    return normalized;
  }

  /**
   * Get all billing records
   * @returns Promise with billing records array
   */
  async getAllBillingRecords(): Promise<BillingRecord[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/billing/records`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching billing records:', error);
      throw error;
    }
  }

  /**
   * Get billing record by ID
   * @param id - Billing record ID
   * @returns Promise with billing record
   */
  async getBillingRecordById(id: string): Promise<BillingRecord> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/billing/records/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching billing record:', error);
      throw error;
    }
  }

  /**
   * Get billing statistics
   * @returns Promise with billing stats
   */
  async getBillingStats(): Promise<BillingStats> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/billing/stats`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching billing stats:', error);
      throw error;
    }
  }

  /**
   * Download invoice PDF
   * @param invoiceId - Invoice ID
   * @returns Promise with blob data
   */
  async downloadInvoice(invoiceId: string): Promise<Blob> {
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token') || '';
      const response = await axios.get(`${API_BASE_URL}/api/v1/billing/invoices/${invoiceId}/download`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      console.error('Error downloading invoice:', error);
      throw error;
    }
  }

  /**
   * Update billing record status
   * @param id - Billing record ID
   * @param status - New status
   * @returns Promise with updated billing record
   */
  async updateBillingStatus(id: string, status: 'Active' | 'Inactive'): Promise<BillingRecord> {
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/api/billing/records/${id}/status`,
        { status },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating billing status:', error);
      throw error;
    }
  }

  /**
   * Get dummy/mock billing data for development
   * @returns Mock billing records
   */
  getMockBillingRecords(): BillingRecord[] {
    return [
      {
        id: '1',
        facilityName: 'Evergreen Health Medical Center - Kirkland, WA',
        selectedPlan: 'Pro Plan',
        status: 'Active',
        invoiceDate: '10/10/2025',
        expiryDate: '10/10/2025',
        invoiceId: '234145ID',
      },
      {
        id: '2',
        facilityName: 'Greenwood Health Clinic - Seattle, WA',
        selectedPlan: 'Enterprise Plan',
        status: 'Active',
        invoiceDate: '11/15/2024',
        expiryDate: '11/15/2024',
        invoiceId: '568912ID',
      },
      {
        id: '3',
        facilityName: 'Lakeside Medical Center - Bellevue, WA',
        selectedPlan: 'Pro Plan (RHC)',
        status: 'Active',
        invoiceDate: '12/20/2025',
        expiryDate: '12/20/2025',
        invoiceId: '789456ID',
      },
      {
        id: '4',
        facilityName: 'Riverbend Health Facility - Redmond, WA',
        selectedPlan: 'Pro Plan',
        status: 'Inactive',
        invoiceDate: '09/01/2023',
        expiryDate: '09/01/2024',
        invoiceId: '321654ID',
      },
      {
        id: '5',
        facilityName: 'Maplewood Community Clinic - Renton, WA',
        selectedPlan: 'Family Plan',
        status: 'Active',
        invoiceDate: '05/30/2025',
        expiryDate: '05/30/2025',
        invoiceId: '987321ID',
      },
    ];
  }

  /**
   * Get mock billing statistics
   * @returns Mock billing stats
   */
  getMockBillingStats(): BillingStats {
    return {
      totalRevenue: 125000,
      activeFacilities: 4,
      inactiveFacilities: 1,
      pendingPayments: 2,
    };
  }
}

// Export singleton instance
export const billingService = new BillingService();
export default billingService;
